import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { usePopper } from 'react-popper';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import { toast } from 'react-toastify';
import { selectUserRolePermissions } from '@socialive/common';
import { selectLegacyBaseUrl } from '~/store';
import { ModalTypes, Pathnames } from '../../helpers/enums';
import ConfirmationModal from '../ConfirmationModal';
import CustomModal from '../CustomModal';
import { guestBroadcastOptions, joinBroadcastOptions, myBroadcastOptions } from './optionsData';

export default function MoreOptions({
  APIBaseURL,
  broadcastHash,
  broadcastID,
  broadcastTitle,
  hasComments,
  reloadBroadcasts,
  token,
}) {
  const { pathname } = useLocation();
  const history = useHistory();
  const { canNavigateInPortal } = useSelector(selectUserRolePermissions);

  const [confirmationValues, setConfirmationValues] = useState({
    modalOpen: false,
    type: '',
  });
  const [renameModal, setRenameModalOpen] = useState(false);

  // popover logic
  const [popperOpen, setPopperOpen] = useState(false);
  const optionsRef = useRef(null);
  const popperRef = useRef(null);

  const legacyBaseUrl = useSelector(selectLegacyBaseUrl);

  useEffect(() => {
    function handleDocumentClick(event) {
      if (!event.target.id.includes(`pop-over-${broadcastHash}`)) {
        setPopperOpen(false);
      }
    }

    document.addEventListener('mousedown', handleDocumentClick);
    return () => {
      document.removeEventListener('mousedown', handleDocumentClick);
    };
  }, [broadcastHash]);

  const { styles, update } = usePopper(optionsRef.current, popperRef.current, {
    placement: 'bottom-end',
    modifiers: [
      {
        name: 'computeStyles',
        options: {
          adaptive: false,
        },
      },
    ],
  });
  // end: popover logic

  let options = guestBroadcastOptions;

  if (canNavigateInPortal) {
    options = pathname === Pathnames.DASHBOARD ? myBroadcastOptions : joinBroadcastOptions;
  }

  if (!hasComments) {
    const foundIndex = options.findIndex(({ id }) => id === 'facebook');
    options = options.filter((_, index) => index !== foundIndex);
  }

  function handleComments() {
    const hash = broadcastHash;
    const id = broadcastID;
    const channelToken = token;
    const iframeSrc = `${legacyBaseUrl}/comments/index.php`;

    sessionStorage.setItem('currentChannelToken', channelToken);
    sessionStorage.setItem('currentBroadcastId', id);
    sessionStorage.setItem('currentBroadcastHash', hash);

    history.push({
      pathname: '/admin/comments',
      state: { iframeSrc },
    });
  }

  function handleOptionClick(id) {
    switch (id) {
      case 'remove':
        setPopperOpen(false);
        setConfirmationValues({ modalOpen: true, type: 'remove' });
        break;
      case 'close':
        setPopperOpen(false);
        setConfirmationValues({ modalOpen: true, type: 'close' });
        break;
      case 'rename':
        setPopperOpen(false);
        setRenameModalOpen(true);
        break;
      case 'facebook':
        setPopperOpen(false);
        handleComments();
        break;
      default:
        break;
    }
  }

  function handleRemoveBroadcast() {
    const URL = `${APIBaseURL}/broadcast/guest/${broadcastHash}/remove`;
    const params = {
      async: true,
      crossDomain: true,
      headers: {
        authorization: sessionStorage.getItem('authToken'),
        'content-type': 'application/json',
        'x-api-version': '2.0',
      },
      method: 'post',
      processData: false,
      body: JSON.stringify({
        email: sessionStorage.getItem('globalUserName'),
      }),
    };

    fetch(URL, params)
      .then((response) => {
        if (response.status !== 200) {
          toast.error('Unable to remove. Please try again.');
          // eslint-disable-next-line no-console
          console.error('Received bad response from removeBroadcastGuest.');
        } else {
          toast.success('Broadcast Invitation Removed');
          reloadBroadcasts();
        }
        setConfirmationValues({ modalOpen: false, type: '' });
      })
      .catch((error) => {
        toast.error('Unable to remove. Please try again.');
        // eslint-disable-next-line no-console
        console.error('guest broadcasts remove API call failed. ', error);
        setConfirmationValues({ modalOpen: false, type: '' });
      });
  }

  function handleCloseBroadcast() {
    const URL = `${APIBaseURL}/broadcast/close/${broadcastHash}`;
    const params = {
      async: true,
      crossDomain: true,
      headers: {
        authorization: sessionStorage.getItem('authToken'),
        'content-type': 'application/json',
        'x-api-version': '2.0',
      },
      method: 'post',
      processData: false,
    };

    fetch(URL, params)
      .then((response) => {
        if (response.status !== 200) {
          toast.error('Unable to close broadcast. Please try again.');
          // eslint-disable-next-line no-console
          console.error('Received bad response from closeBroadcast.');
        } else {
          toast.success('Broadcast Closed');
          reloadBroadcasts();
        }
        setConfirmationValues({ modalOpen: false, type: '' });
      })
      .catch((error) => {
        toast.error('Unable to close broadcast. Please try again.');
        // eslint-disable-next-line no-console
        console.error('close broadcast API call failed. ', error);
        setConfirmationValues({ modalOpen: false, type: '' });
      });
  }

  function handleRename(newTitle) {
    const URL = `${APIBaseURL}/broadcasts/${broadcastID}`;
    const params = {
      async: true,
      crossDomain: true,
      headers: {
        authorization: sessionStorage.getItem('authToken'),
        'content-type': 'application/json',
        'x-api-version': '2.0',
      },
      method: 'PATCH',
      processData: false,
      body: JSON.stringify({
        name: newTitle,
      }),
    };

    fetch(URL, params)
      .then((response) => {
        if (response.status !== 200) {
          toast.error('Unable to rename. Please try again.');
          // eslint-disable-next-line no-console
          console.error('Received bad response from renameBroadcastTitle.');
        } else {
          toast.success('Broadcast Title Renamed');
          reloadBroadcasts();
        }
        setRenameModalOpen(false);
      })
      .catch((error) => {
        toast.error('Unable to rename. Please try again.');
        // eslint-disable-next-line no-console
        console.error('rename broadcast title API call failed. ', error);
        setRenameModalOpen(false);
      });
  }

  const isRemovalRequest = confirmationValues.type === 'remove';

  return (
    <div className="more-options-icon-container">
      <button
        type="button"
        id={`pop-over-${broadcastHash}-icon`}
        className="more-options-icon"
        aria-label="More Options Button"
        onClick={() => {
          setPopperOpen(!popperOpen);
          if (update) {
            update();
          }
        }}
        ref={optionsRef}
      />
      <div ref={popperRef} style={styles.popper} className="pop-over-ref">
        <div style={{ display: popperOpen ? 'inline' : 'none' }} visible={popperOpen.toString()}>
          <div id={`pop-over-${broadcastHash}-container`} className="more-options-popover">
            {options.map((o) => (
              <button
                key={o.id}
                type="button"
                id={`pop-over-${broadcastHash}-${o.id}`}
                className="more-options-selection"
                style={{ color: o.id === 'remove' || o.id === 'close' ? 'red' : 'grey' }}
                onClick={() => handleOptionClick(o.id)}
              >
                {o.text}
              </button>
            ))}
          </div>
        </div>
      </div>
      <ConfirmationModal
        isOpen={confirmationValues.modalOpen}
        handleOnClose={() => setConfirmationValues({ modalOpen: false, type: '' })}
        handleOnConfirm={isRemovalRequest ? handleRemoveBroadcast : handleCloseBroadcast}
        resourceDescription={
          isRemovalRequest
            ? 'Are you sure you want to be removed from this broadcast?'
            : 'If you close the broadcast, you and your guests will no longer be able to join.'
        }
        confirmButtonText={isRemovalRequest ? 'Remove' : 'Close'}
        iconName={isRemovalRequest ? 'icon-logout' : 'icon-remove-circle'}
      />
      <CustomModal
        isOpen={renameModal}
        modalTitle="Edit title"
        type={ModalTypes.INPUT}
        inputDefaultValue={broadcastTitle}
        inputTitle="TITLE"
        maxCharLength={128}
        confirmButtonText="Save"
        closeButtonText="Cancel"
        handleOnConfirm={handleRename} //eslint-disable-line
        handleOnClose={() => setRenameModalOpen(false)}
      />
    </div>
  );
}

MoreOptions.propTypes = {
  APIBaseURL: PropTypes.string.isRequired,
  broadcastHash: PropTypes.string.isRequired,
  broadcastID: PropTypes.number.isRequired,
  broadcastTitle: PropTypes.string.isRequired,
  hasComments: PropTypes.bool.isRequired,
  reloadBroadcasts: PropTypes.func.isRequired,
  token: PropTypes.string.isRequired,
};
